import moment from 'moment-timezone';
import { getDateKey, weekStartDateKeyFor } from './utils';

/** Site-timezone calendar helpers for computeCalendarViewData. */
export type SiteCalendarClock = {
  siteTimezone: string;
  /** CPF effectiveDate/endDate: UTC instant → site calendar day start. */
  parseCpfDate: (iso: string) => Date;
  /** FullCalendar range boundaries (YYYY-MM-DD) in site timezone. */
  parseCalendarDate: (iso: string) => Date;
  parseVisitDate: (visit: mumms.pimreaderVisit) => Date | null;
  visitDateKey: (visit: mumms.pimreaderVisit) => string | null;
  todayStart: () => Date;
  addDays: (date: Date, n: number) => Date;
  addCalendarMonths: (date: Date, n: number) => Date;
  dayOfWeek: (date: Date) => number;
  dateKey: (date: Date) => string;
  weekStartDateKey: (date: Date, weekStartDayIndex: number) => string;
};

const startOfSiteDay = (m: moment.Moment): Date =>
  m.clone().startOf('day').toDate();

export const createSiteCalendarClock = (
  siteTimezone: string,
): SiteCalendarClock => {
  const dateKey = (date: Date): string => getDateKey(date, siteTimezone);

  const parseCpfDate = (iso: string): Date =>
    startOfSiteDay(moment.utc(iso).tz(siteTimezone));

  const parseCalendarDate = (iso: string): Date =>
    startOfSiteDay(moment.tz(iso.slice(0, 10), 'YYYY-MM-DD', siteTimezone));

  const parseVisitDate = (visit: mumms.pimreaderVisit): Date | null => {
    // effectivedatetime_shifted is the actual visit date in site TZ.
    // starteventdatetime_shifted is the creation/checkin event time — not the visit date.
    if (visit.effectivedatetime_shifted) {
      return startOfSiteDay(
        moment.tz(visit.effectivedatetime_shifted, siteTimezone),
      );
    }

    const fallback = visit.effectivedate;
    if (!fallback) return null;

    if (fallback.includes('T')) {
      return startOfSiteDay(moment.utc(fallback).tz(siteTimezone));
    }

    return startOfSiteDay(
      moment.tz(fallback.slice(0, 10), 'YYYY-MM-DD', siteTimezone),
    );
  };

  const visitDateKey = (visit: mumms.pimreaderVisit): string | null => {
    const visitDate = parseVisitDate(visit);
    return visitDate ? dateKey(visitDate) : null;
  };

  return {
    siteTimezone,
    parseCpfDate,
    parseCalendarDate,
    parseVisitDate,
    visitDateKey,
    todayStart: () => startOfSiteDay(moment.tz(siteTimezone)),
    addDays: (date, n) =>
      startOfSiteDay(moment.tz(date, siteTimezone).add(n, 'days')),
    addCalendarMonths: (date, n) =>
      startOfSiteDay(moment.tz(date, siteTimezone).add(n, 'months')),
    dayOfWeek: (date) => moment.tz(date, siteTimezone).day(),
    dateKey,
    weekStartDateKey: (date, weekStartDayIndex) =>
      weekStartDateKeyFor(date, weekStartDayIndex, siteTimezone),
  };
};
